import { client } from '../../default.client';
import getFilials from './get-filials';
import { JSON_API_HEADER } from '../../constants';
import { utcToLocalTime } from '@/utils/functions/utc-to-local-time';
import { getKioskUrl } from '@/utils/functions/kiosk-url';
import type {
	ApiProxyResponse,
	ICreateOrderResponse
} from '../../types/interfaces';
import type { IFilialSettings } from '@/types/interfaces/navigator';
import type {
	ICreateDeliveryOrderPayload,
	ICreateExpressOrderPayload,
	ICreateTakeawayOrderPayload,
	ICreateVendorDeliveryOrderPayload,
	IOrderDetails,
	IOrderItem,
	IProductForApi
} from '@/types/interfaces/orders';
import type { IProduct } from '@/types/interfaces/menu';

const getOrderDetailsAdapter = (
	data: ApiProxyResponse<IOrderDetails>
): ApiProxyResponse<IOrderDetails> => {
	const { created_at, current_order_status } = data.data.attributes;
	data.data.attributes.created_at = utcToLocalTime(created_at);

	const { next_average_time, next_status_time } = current_order_status;
	if (next_average_time) {
		data.data.attributes.current_order_status.next_average_time =
			utcToLocalTime(next_average_time);
	}
	if (next_status_time) {
		data.data.attributes.current_order_status.next_status_time =
			utcToLocalTime(next_status_time);
	}

	return data;
};

const getOrdersAdapter = (data: ApiProxyResponse<IOrderItem[]>) => {
	data.data.map(item => {
		const { created_at } = item.attributes;
		item.attributes.created_at = utcToLocalTime(created_at);

		return item;
	});

	return data;
};

export const takeaway = {
	async createDeliveryOrder(
		payload: ICreateDeliveryOrderPayload,
		idempotencyKey?: string
	) {
		const response = await client.post<ApiProxyResponse<ICreateOrderResponse>>(
			'takeaway/v2/delivery/order',
			payload,
			{
				headers: {
					'X-Idempotency-key': idempotencyKey
				}
			}
		);
		return response.data;
	},
	async createExpressOrder(
		payload: ICreateExpressOrderPayload,
		idempotencyKey?: string
	) {
		const response = await client.post<ApiProxyResponse<ICreateOrderResponse>>(
			getKioskUrl('takeaway/v2/express/order'),
			payload,
			{
				headers: {
					'X-Idempotency-key': idempotencyKey
				}
			}
		);
		return response.data;
	},
	async createMicromarketOrder(
		payload: ICreateTakeawayOrderPayload,
		idempotencyKey?: string
	) {
		const response = await client.post<ApiProxyResponse<ICreateOrderResponse>>(
			'takeaway/v2/micromarket/order',
			payload,
			{
				headers: {
					'X-Idempotency-key': idempotencyKey
				}
			}
		);
		return response.data;
	},
	async createOrder(
		payload: ICreateTakeawayOrderPayload,
		idempotencyKey?: string
	) {
		const response = await client.post<ApiProxyResponse<ICreateOrderResponse>>(
			getKioskUrl('takeaway/v2/takeaway/order'),
			payload,
			{
				headers: {
					'X-Idempotency-key': idempotencyKey
				}
			}
		);
		return response.data;
	},
	async createVendorDeliveryOrder(
		payload: ICreateVendorDeliveryOrderPayload,
		idempotencyKey?: string
	) {
		const response = await client.post<ApiProxyResponse<ICreateOrderResponse>>(
			'takeaway/v2/vendor-delivery/order',
			payload,
			{ headers: { 'X-Idempotency-key': idempotencyKey } }
		);
		return response.data;
	},
	async getFilialBanner(
		filialId: number,
		abort?: AbortController
	): Promise<ApiProxyResponse<IFilialSettings>> {
		const url = `takeaway/v2/filial/settings/banner/info/${filialId}?is_web_view=1`;

		const config = {
			headers: JSON_API_HEADER,
			signal: abort?.signal
		};

		const response = await client.get(url, { ...config });
		return response.data;
	},
	getFilials,
	async getFrequentProducts(
		filialId: number,
		abort?: AbortController
	): Promise<ApiProxyResponse<{ products: IProduct[] }>> {
		const config = {
			headers: JSON_API_HEADER,
			signal: abort?.signal
		};

		const response = await client.get(
			`takeaway/v1/filial/${filialId}/products/frequent/`,
			{
				...config,
				params: {
					limit: 20
				}
			}
		);
		return response.data;
	},
	async getOrderCookingTime(
		filialId: number,
		products: IProductForApi[]
	): Promise<
		ApiProxyResponse<{
			cooking_time?: number;
			is_high_cooking_time: boolean;
		}>
	> {
		const response = await client.post(
			'takeaway/order/cooking_time',
			{ filial_id: filialId, products },
			{ headers: JSON_API_HEADER }
		);
		return response.data;
	},
	async getOrderDetails(
		orderId: number
	): Promise<ApiProxyResponse<IOrderDetails>> {
		const response = await client.get(
			getKioskUrl(`takeaway/v2/user/order?order_id=${orderId}`),
			{ headers: JSON_API_HEADER }
		);
		return getOrderDetailsAdapter(response.data);
	},
	async getOrders(
		page = 1,
		serviceType = 1,
		brandId?: string
	): Promise<ApiProxyResponse<IOrderItem[]>> {
		const params = {
			page,
			service_type: serviceType,
			brand_id: brandId
		};

		const response = await client.get(getKioskUrl('takeaway/v2/user/orders'), {
			headers: JSON_API_HEADER,
			params
		});

		return getOrdersAdapter(response.data);
	}
};
